/* ============================================================
   NABDI — Interactions
   ============================================================ */

(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Header: scroll behavior ---------- */
  const header = $('#nabdiHeader');
  const topbar = $('#nabdiTopbar');
  let lastScrollY = window.scrollY;
  let scrollThreshold = 80;

  const handleScroll = () => {
    const y = window.scrollY;
    const goingDown = y > lastScrollY && y > 240;

    if (header) header.classList.toggle('is-scrolled', y > scrollThreshold);
    if (topbar) topbar.classList.toggle('is-hidden', y > scrollThreshold);

    lastScrollY = y;
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();

  /* ---------- Magnetic nav pill ---------- */
  const nav = $('#nabdiNav');
  if (nav) {
    const pill = nav.querySelector('.nabdi-nav-pill');
    const items = $$('[data-nav]', nav);

    const movePill = (target) => {
      if (!target || !pill) return;
      const navRect = nav.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const x = targetRect.left - navRect.left;
      const w = targetRect.width;
      pill.style.width = `${w}px`;
      pill.style.transform = `translateX(${x}px)`;
    };

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        nav.classList.add('is-hovering');
        items.forEach((el) => el.classList.remove('is-hovered'));
        item.classList.add('is-hovered');
        movePill(item);
      });
    });

    nav.addEventListener('mouseleave', () => {
      nav.classList.remove('is-hovering');
      items.forEach((el) => el.classList.remove('is-hovered'));
    });
  }

  /* ---------- Mobile menu ---------- */
  const burger = $('#nabdiBurger');
  const mobileMenu = $('#nabdiMobileMenu');
  const mobileClose = $('#nabdiMobileClose');
  const mobileLinks = $$('[data-mobile-link]');

  const openMobileMenu = () => {
    if (!mobileMenu || !burger) return;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    burger.classList.add('is-open');
    burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
  };

  const closeMobileMenu = () => {
    if (!mobileMenu || !burger) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    burger.classList.remove('is-open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
  };

  if (burger) {
    burger.addEventListener('click', () => {
      const isOpen = mobileMenu?.classList.contains('is-open');
      isOpen ? closeMobileMenu() : openMobileMenu();
    });
  }

  if (mobileClose) mobileClose.addEventListener('click', closeMobileMenu);
  mobileLinks.forEach((link) => link.addEventListener('click', closeMobileMenu));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileMenu?.classList.contains('is-open')) {
      closeMobileMenu();
    }
  });

  /* ---------- Live time (locale-aware: FR=Paris / AR=Dubai) ---------- */
  const timeEl = $('#nabdiTimeValue');
  let currentTz = 'Europe/Paris';
  let timeFormatter = null;

  const buildFormatter = (tz) =>
    new Intl.DateTimeFormat('fr-FR', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

  const updateTime = () => {
    if (!timeEl || !timeFormatter) return;
    timeEl.textContent = timeFormatter.format(new Date());
  };

  const setTimeZone = (locale) => {
    currentTz = locale === 'ar' ? 'Asia/Dubai' : 'Europe/Paris';
    timeFormatter = buildFormatter(currentTz);
    updateTime();
  };

  if (timeEl) {
    setTimeZone('fr');
    setInterval(updateTime, 30_000);
  }

  /* ---------- Locale toggle (FR / AR) ---------- */
  $$('[data-locale]').forEach((btn) => {
    btn.addEventListener('click', () => {
      $$('[data-locale]').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      setTimeZone(btn.dataset.locale);
    });
  });

  /* ---------- Reveal animations on scroll ---------- */
  if ('IntersectionObserver' in window) {
    const reveals = $$('.reveal, .reveal-stagger');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    reveals.forEach((el) => io.observe(el));
  } else {
    $$('.reveal, .reveal-stagger').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Active nav item on scroll ---------- */
  const sections = $$('section[id]');
  const navLinks = $$('[data-nav]');

  if (sections.length && navLinks.length && 'IntersectionObserver' in window) {
    const sectionIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            navLinks.forEach((link) => {
              const isActive = link.getAttribute('href') === `#${id}`;
              link.classList.toggle('is-active', isActive);
            });
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach((s) => sectionIO.observe(s));
  }

  /* ---------- Smooth scroll for anchor links (with header offset) ---------- */
  $$('a[href*="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href === '#' || href === '/#') return;

      const hashIndex = href.indexOf('#');
      const path = href.slice(0, hashIndex);
      const hash = href.slice(hashIndex);

      const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
      const linkPath = path.replace(/\/$/, '') || '/';
      const isInPage = path === '' || linkPath === currentPath;

      if (!isInPage) return;

      const target = document.querySelector(hash);
      if (!target) return;

      e.preventDefault();
      const headerOffset = 80;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
      history.pushState(null, '', hash);
    });
  });

  /* ---------- Product gallery (thumbs swap) ---------- */
  const gallery = $('#productGallery');
  if (gallery) {
    const mainImages = $$('.product-gallery-main img', gallery);
    const thumbs = $$('.product-gallery-thumb', gallery);

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = thumb.dataset.thumb;
        mainImages.forEach((img) => img.classList.toggle('is-active', img.dataset.index === index));
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      });
    });
  }

  /* ---------- Cart page (qty +/-, remove) ---------- */
  const cartForm = $('#nabdiCartForm');
  if (cartForm) {
    const updateCart = async (key, quantity) => {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (res.ok) {
          window.location.reload();
        }
      } catch (e) {
        console.error('Cart update failed', e);
      }
    };

    cartForm.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-cart-minus]');
      const plus = e.target.closest('[data-cart-plus]');
      const remove = e.target.closest('[data-cart-remove]');

      if (minus) {
        const key = minus.dataset.cartMinus;
        const input = cartForm.querySelector(`[data-cart-qty="${key}"]`);
        const v = Math.max(0, parseInt(input.value, 10) - 1);
        updateCart(key, v);
      }

      if (plus) {
        const key = plus.dataset.cartPlus;
        const input = cartForm.querySelector(`[data-cart-qty="${key}"]`);
        const v = Math.min(99, parseInt(input.value, 10) + 1);
        updateCart(key, v);
      }

      if (remove) {
        const key = remove.dataset.cartRemove;
        updateCart(key, 0);
      }
    });
  }

  /* ---------- Product quantity + sticky ATC ---------- */
  const qtyInput = $('#nabdiQty');
  if (qtyInput) {
    const minus = $('[data-qty-minus]');
    const plus = $('[data-qty-plus]');

    minus?.addEventListener('click', () => {
      const v = Math.max(1, parseInt(qtyInput.value, 10) - 1);
      qtyInput.value = v;
    });
    plus?.addEventListener('click', () => {
      const v = Math.min(99, parseInt(qtyInput.value, 10) + 1);
      qtyInput.value = v;
    });
  }

  const productForm = $('#nabdiProductForm');
  const stickyAtc = $('#productStickyAtc');
  if (productForm && stickyAtc) {
    const stickyBtn = $('[data-sticky-atc]', stickyAtc);

    stickyBtn?.addEventListener('click', () => {
      productForm.requestSubmit();
    });

    if ('IntersectionObserver' in window) {
      const productInfo = $('.product-info');
      if (productInfo) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '-100px 0px 0px 0px' }
        );
        io.observe(productInfo);
      }
    }
  }
})();
